import { useCallback, useEffect, useRef, useState } from 'react'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useToastContext } from 'src/components/ui/ToastMessage/ToastContext'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getCookie, deleteCookie } from 'src/utils/cookies'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import formatTotalizersTotal from 'src/utils/amplitude/formatTotalizersTotal'
import { getCartItemsCount } from 'src/utils/amplitude/cartEventValues'
import { buildCheckoutPaymentUrl } from 'src/utils/checkout/buildCheckoutPaymentUrl'

import storeConfig from '../../../../store.config'

let abandonedToastClaimed = false

function claimAbandonedToast() {
  if (abandonedToastClaimed) {
    return false
  }

  abandonedToastClaimed = true

  return true
}

function releaseAbandonedToast() {
  abandonedToastClaimed = false
}

function getTimeSinceCheckoutAbandonment() {
  const lastCheckoutVisit = localStorage.getItem('lastCheckoutVisit')

  if (!lastCheckoutVisit) {
    return null
  }

  const diffMs = Date.now() - parseInt(lastCheckoutVisit, 10)

  return Math.floor(diffMs / 1000)
}

const AbandonedCart = () => {
  const { orderForm } = useCheckout()
  const { sendToast } = useToastContext()
  const { pageData } = useGlobalContext()

  const hasShownToast = useRef(false)
  const toastStartTime = useRef<number | null>(null)
  const reopenTimerRef = useRef<NodeJS.Timeout | null>(null)
  const showTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [toastTrigger, setToastTrigger] = useState(0)

  const [abandonedPayment, setAbandonedPayment] = useState('')

  useEffect(() => {
    setAbandonedPayment(getCookie('AbandonedPayment') ?? '')
  }, [])

  const checkoutUrl = useCallback(() => {
    const { clientProfileData, shippingData, orderFormId } = orderForm ?? {}

    if (!clientProfileData) {
      return '/checkout/profile'
    }

    if (!shippingData?.address) {
      return '/checkout/shipping'
    }

    const baseUrl = storeConfig?.checkoutUrl ?? '/checkout'

    return buildCheckoutPaymentUrl(baseUrl, {
      orderFormId,
      payment_recovery: 'true',
    })
  }, [orderForm])

  const reopenDelay = 10 * 60 * 1000

  useEffect(() => {
    const showToast = () => {
      if (!abandonedPayment || hasShownToast.current) {
        return
      }

      if (abandonedToastClaimed) {
        hasShownToast.current = true

        return
      }

      if (orderForm?.items?.length === 0) {
        localStorage.removeItem('abandoned-toast-dismissed')
        deleteCookie('AbandonedPayment')

        return
      }

      const standardObject = getStandardObject(pageData)
      const firstItem = orderForm?.items?.[0]

      toastStartTime.current = Date.now()

      const handleEventClick = () => {
        const now = Date.now()
        const timeVisibleBeforeClick = toastStartTime.current
          ? Math.floor((now - toastStartTime.current) / 1000)
          : null

        localStorage.setItem('abandoned-toast-dismissed', 'true')

        dispatchAmplitudeEvent({
          eventName: 'Payment Reminder Toast CTA Clicked',
          eventData: {
            ...standardObject,
            'time since abandonment seconds': getTimeSinceCheckoutAbandonment(),
            'number cart items': getCartItemsCount(orderForm?.items),
            'value cart items': (
              Number(orderForm?.totalizers?.total?.replace(/\D/g, '') ?? 0) /
              100
            ).toFixed(2),
            'time visible before click': timeVisibleBeforeClick,
          },
        })

        window.dataLayer.push({
          event: 'event_track',
          event_category: 'clique',
          event_action: 'Retomar o pagamento',
          event_label: 'Popup',
        })
      }

      if (!claimAbandonedToast()) {
        hasShownToast.current = true

        return
      }

      sendToast({
        text: 'Seu carrinho te espera',
        subtext: (
          <a
            href={checkoutUrl()}
            className="text-sm text-blue-600 font-medium hover:underline text-[#EFF1F3] leading-[120%]"
            onClick={handleEventClick}
          >
            Retomar o pagamento &gt;
          </a>
        ),
        image: firstItem?.imageUrl,
        variant: 'abandoned',
        persist: true,
        orderFormInfo: orderForm,
        toastStartTime: toastStartTime.current,
        onClose: (reason: 'clicked_x') => {
          const now = Date.now()
          const timeVisibleBeforeClose = toastStartTime.current
            ? Math.floor((now - toastStartTime.current) / 1000)
            : null

          dispatchAmplitudeEvent({
            eventName: 'Payment Reminder Toast Closed',
            eventData: {
              ...standardObject,
              'time since abandonment seconds':
                getTimeSinceCheckoutAbandonment(),
              'number cart items': getCartItemsCount(orderForm?.items),
              'value cart items': (
                Number(orderForm?.totalizers?.total?.replace(/\D/g, '') ?? 0) /
                100
              ).toFixed(2),
              'time visible before close': timeVisibleBeforeClose,
              'dismissal reason': reason,
            },
          })

          if (reopenTimerRef.current) {
            clearTimeout(reopenTimerRef.current)
          }

          reopenTimerRef.current = setTimeout(() => {
            releaseAbandonedToast()
            hasShownToast.current = false
            setToastTrigger((prev) => prev + 1)
          }, reopenDelay)
        },
      })

      hasShownToast.current = true

      dispatchAmplitudeEvent({
        eventName: 'Payment Reminder Toast Displayed',
        eventData: {
          ...standardObject,
          'time since abandonment seconds': getTimeSinceCheckoutAbandonment(),
          'number cart items': getCartItemsCount(orderForm?.items),
          'value cart items': formatTotalizersTotal(
            orderForm?.totalizers?.total ?? 0
          ),
        },
      })
    }

    showTimerRef.current = setTimeout(showToast, 2000)

    return () => {
      clearTimeout(showTimerRef.current!)
      clearTimeout(reopenTimerRef.current!)
    }
  }, [
    abandonedPayment,
    checkoutUrl,
    orderForm,
    pageData,
    reopenDelay,
    sendToast,
    toastTrigger,
  ])

  return null
}

export default AbandonedCart
